import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Link,
  Typography,
  Alert,
} from '@mui/material';
import { Warning as WarningIcon } from '@mui/icons-material';
import type { OpenWorkflowSummary } from '../../types/workflow';

interface WorkflowDuplicateWarningDialogProps {
  open: boolean;
  duplicates: OpenWorkflowSummary[];
  onCreateAnyway: () => void;
  onCancel: () => void;
}

/**
 * Dialog warning users about existing open workflows for the same farmer+farm.
 * Shown before workflow creation when duplicates are detected.
 */
export const WorkflowDuplicateWarningDialog: React.FC<WorkflowDuplicateWarningDialogProps> = ({
  open,
  duplicates,
  onCreateAnyway,
  onCancel,
}) => {
  return (
    <Dialog open={open} onClose={onCancel} maxWidth="md" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <WarningIcon color="warning" />
        <Typography variant="h6" component="span">
          Possible Duplicate Workflows
        </Typography>
      </DialogTitle>
      <DialogContent>
        <Alert severity="warning" sx={{ mb: 2 }}>
          Open workflows already exist for this farmer and farm. You may be creating a duplicate.
        </Alert>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Reference</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Farm</TableCell>
              <TableCell>Created</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {duplicates.map((d) => (
              <TableRow key={d.workflowId}>
                <TableCell>
                  <Link
                    href={`/workflow/${d.workflowId}`}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {d.referenceNumber || `#${d.workflowId}`}
                  </Link>
                </TableCell>
                <TableCell>{d.statusName || '-'}</TableCell>
                <TableCell>{d.farmName || '-'}</TableCell>
                <TableCell>
                  {new Date(d.createdAt).toLocaleDateString()}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </DialogContent>
      <DialogActions sx={{ p: 2 }}>
        <Button onClick={onCancel} variant="outlined">
          Cancel
        </Button>
        <Button onClick={onCreateAnyway} variant="contained" color="warning">
          Create Anyway
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default WorkflowDuplicateWarningDialog;
